import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { LoginResponse } from '@/api';
import { useNullableTokens } from '@/common/providers/tokens-provider';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { getAppCheckTokenHeaders, isFirebaseError } from '@/firebase';
import { UIToast } from '@/ui';
import { useMeTranslation } from '../hooks';

export type LoginVariables = {
    email: string;
    password: string;
};

type UseLoginActionData = {
    login: (variables: LoginVariables) => Promise<LoginResponse>;
    loading: boolean;
    error: string;
    /** Clears the message so a fresh attempt starts on a clean form. */
    clearError: () => void;
};

/**
 * Signing in with an email and password. Storing the tokens is what actually
 * signs the user in — every gate downstream reads them.
 */
export function useLoginAction(): UseLoginActionData {
    const { t } = useMeTranslation();
    const { setTokens } = useNullableTokens();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `POST /v3/auth/login`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            // Thrown before the request, while minting the App Check token.
            if (isFirebaseError(error)) {
                return t('shared.errors.app_check_failed');
            }

            if (isApiError(error, 'APP_CHECK_FAILED')) {
                return t('shared.errors.app_check_failed');
            }
            if (isApiError(error, 'INVALID_CREDENTIALS')) {
                return t('shared.errors.login_incorrect');
            }
            if (isApiError(error, 'TOO_MANY_REQUESTS')) {
                return t('shared.errors.login_too_many_attempts');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: ({ email, password }: LoginVariables) =>
            // Login is App Check protected, so the token has to be fetched
            // before the request rather than added by an interceptor.
            getAppCheckTokenHeaders()
                .then((headers) =>
                    openApiRef.current.auth.login(
                        { email, password },
                        { headers }
                    )
                )
                .then((response) => response.data),
        onSuccess: (data) => {
            const { accessToken, refreshToken } = data.data;
            setTokens({ accessToken, refreshToken });
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        login: (variables) => mutation.mutateAsync(variables),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : '',
        clearError: () => mutation.reset()
    };
}
